"use client";

import { useState } from "react";
import Link from "next/link";
import NumberFlow from "@number-flow/react";
import { Icon } from "@/components/ui/icon";
import { PlanIcon } from "@/components/ui/plan-icon";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";
import { CREDIT_BREAKDOWN_HREF } from "../lib/credit-breakdown";
import { CancelFlow } from "./cancel-flow";
import {
  useCancelDowngrade,
  type SubscriptionResponse,
  type CreditBalanceResponse,
} from "../hooks/use-subscription";

interface PlanStatusBarProps {
  subscription: SubscriptionResponse | null;
  credits: CreditBalanceResponse | null;
}

export function PlanStatusBar({ subscription, credits }: PlanStatusBarProps) {
  const [showCancel, setShowCancel] = useState(false);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CurrentPlanCard
          subscription={subscription}
          onCancelClick={() => setShowCancel(true)}
        />
        <CreditUsageCard credits={credits} subscription={subscription} />
      </div>
      <CancelFlow
        open={showCancel}
        onClose={() => setShowCancel(false)}
        periodEndDate={subscription?.current_period_end ?? null}
      />
    </>
  );
}

function CurrentPlanCard({
  subscription,
  onCancelClick,
}: {
  subscription: SubscriptionResponse | null;
  onCancelClick: () => void;
}) {
  const cancelDowngrade = useCancelDowngrade();
  const planName = subscription?.plan.name ?? "Free";
  const planSlug = subscription?.plan.slug ?? "free";
  const isFree = planSlug === "free";
  const isCanceling = subscription?.cancel_at != null;
  const pendingDowngrade = subscription?.pending_downgrade_plan ?? null;
  const pendingAt = subscription?.pending_downgrade_at ?? null;

  const gradientColors: Record<string, string> = {
    free: "rgba(145, 248, 136, 0.15)",
    solo: "rgba(84, 245, 237, 0.15)",
    growth: "rgba(255, 175, 83, 0.15)",
    business: "rgba(43, 93, 243, 0.15)",
  };
  const gradientColor = gradientColors[planSlug] ?? "rgba(255,255,255,0.05)";

  const billingCadence = subscription?.billing_cadence;

  const handleCancelDowngrade = () => {
    cancelDowngrade.mutate(undefined, {
      onSuccess: () => showToast.success("Downgrade cancelled"),
      onError: (err) => showToast.error("Couldn't cancel the downgrade", { description: getApiErrorMessage(err) }),
    });
  };

  return (
    <div className="relative flex min-h-20 items-center gap-3 overflow-hidden rounded-xl border border-border p-4 dark:border-white/10">
      <div
        className="pointer-events-none absolute inset-0 rounded-xl"
        style={{
          background: `linear-gradient(135deg, transparent 40%, ${gradientColor})`,
        }}
      />
      <div className="pointer-events-none absolute inset-0 rounded-xl dark:bg-white/1" />
      <PlanIcon slug={planSlug} className="relative size-10 shrink-0" />
      <div className="relative flex flex-1 flex-col gap-1">
        <span className="text-base font-semibold tracking-[-0.32px] text-heading dark:text-white">
          You&apos;re on the {planName} Plan
          {!isFree && billingCadence
            ? ` (${billingCadence === "monthly" ? "Monthly" : "Annual"})`
            : ""}
        </span>
        {pendingDowngrade ? (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm text-[#c2710a] dark:text-[#FFAF53]">
              Downgrading to {pendingDowngrade.name} on{" "}
              {pendingAt
                ? new Date(pendingAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                : "renewal"}
            </span>
            <button
              type="button"
              onClick={handleCancelDowngrade}
              disabled={cancelDowngrade.isPending}
              className="cursor-pointer text-sm text-contrast/40 underline transition-colors hover:text-heading dark:text-white/40 dark:hover:text-white"
            >
              Keep current plan
            </button>
          </div>
        ) : isFree ? (
          <span className="text-sm text-contrast/60 dark:text-white/70">Upgrade anytime</span>
        ) : isCanceling ? (
          <span className="text-sm text-red-600/80 dark:text-red-400/80">
            Cancels on{" "}
            {new Date(subscription!.cancel_at!).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </span>
        ) : (
          <button
            type="button"
            onClick={onCancelClick}
            className="w-fit cursor-pointer text-left text-sm text-contrast/40 transition-colors hover:text-red-600 dark:hover:text-red-400 dark:text-white/40"
          >
            Cancel plan
          </button>
        )}
      </div>
    </div>
  );
}

function CreditUsageCard({
  credits,
  subscription,
}: {
  credits: CreditBalanceResponse | null;
  subscription: SubscriptionResponse | null;
}) {
  const balance = credits?.balance ?? 0;
  const total = credits?.total_allocation ?? 0;
  const nextCycle = credits?.next_cycle_allocation ?? total;
  const used = Math.max(total - balance, 0);
  const usedPercent = total > 0 ? (used / total) * 100 : 0;
  const showRenewalHint = nextCycle !== total && total > 0;

  const planSlug = subscription?.plan.slug ?? "free";
  const cadence = subscription?.billing_cadence;
  const periodPhrase =
    planSlug === "free"
      ? "lifetime"
      : cadence === "annual"
        ? "this year"
        : "this month";

  const renewalDate = credits?.period_end
    ? new Date(credits.period_end).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <div className="flex min-h-20 flex-col justify-center gap-2.5 rounded-lg border border-border bg-surface-inset px-4 py-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:border-white/10 dark:bg-white/2">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative flex size-[35px] shrink-0 items-center justify-center overflow-hidden rounded-[7px] border border-border bg-contrast/4 dark:border-white/5 dark:bg-white/4">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[rgba(255,175,83,0.1)]" />
            <Icon
              name="IconCoin1"
              size={20}
              className="relative text-[#c2710a] dark:text-[#FFAF53]"
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[14px] font-medium leading-tight text-heading dark:text-white">
              Total credit usage
            </span>
            <span className="flex items-baseline gap-1 text-[13px] leading-tight">
              <span className="font-medium text-heading dark:text-white">
                <NumberFlow value={used} /> of {total.toLocaleString()} credits
              </span>
              <span className="text-contrast/60 dark:text-white/60">used · {periodPhrase}</span>
            </span>
            {showRenewalHint && (
              <span className="text-[11px] leading-none text-contrast/40 dark:text-white/30">
                Renews at {nextCycle.toLocaleString()} credits
                {renewalDate ? ` on ${renewalDate}` : ""}
              </span>
            )}
          </div>
        </div>
        {/* Where the credits went. A free org has no subscription row, so
            this is its only way into the breakdown page. */}
        <Link
          href={CREDIT_BREAKDOWN_HREF}
          data-testid="credit-breakdown-link"
          className="flex shrink-0 items-center gap-1 transition-opacity hover:opacity-80"
        >
          <span className="text-[13px] tracking-[-0.26px] text-contrast/70 dark:text-white/80">
            Breakdown
          </span>
          <Icon name="IconArrowUpRight" size={14} className="text-contrast/70 dark:text-white/80" />
        </Link>
      </div>
      <div className="h-[6px] w-full rounded-full bg-contrast/15 dark:bg-white/[0.15]">
        <div
          className="h-full rounded-full bg-[#ffaf53] transition-all duration-500"
          style={{
            width: `${Math.min(usedPercent, 100)}%`,
            minWidth: usedPercent > 0 ? "8px" : undefined,
          }}
        />
      </div>
    </div>
  );
}
